/**
 * Retry banner that surfaces when the Create page hands off failed draft-job
 * payloads via sessionStorage. Keyed by sessionId. Clears itself when retries
 * succeed or the user dismisses. On-system warning banner (skill).
 */

import React, { useEffect, useState } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { CheckInDraftJobCreate } from '@techgitter/contracts';

import { addCheckInDraftJob } from '../../../services/checkInApi';

interface FailedDraftJobEntry {
  clientId: string;
  payload: CheckInDraftJobCreate;
  error: string;
}

export interface CheckInRetryBannerProps {
  sessionId: string;
  onRetrySucceeded: () => void;
}

export const CheckInRetryBanner: React.FC<CheckInRetryBannerProps> = ({
  sessionId,
  onRetrySucceeded,
}) => {
  const [failed, setFailed] = useState<FailedDraftJobEntry[]>([]);
  const [retrying, setRetrying] = useState(false);
  const key = `sm4rt:checkin:retry:${sessionId}`;

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const parsed: FailedDraftJobEntry[] = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFailed(parsed);
        }
      }
    } catch {
      /* malformed entry — ignore */
    }
  }, [key]);

  if (failed.length === 0) return null;

  const handleRetry = async () => {
    setRetrying(true);
    const stillFailed: FailedDraftJobEntry[] = [];
    for (const entry of failed) {
      try {
        await addCheckInDraftJob(sessionId, entry.payload);
      } catch (err) {
        stillFailed.push({
          ...entry,
          error: err instanceof Error ? err.message : 'Retry failed',
        });
      }
    }
    setFailed(stillFailed);
    if (stillFailed.length === 0) {
      sessionStorage.removeItem(key);
      onRetrySucceeded();
    } else {
      sessionStorage.setItem(key, JSON.stringify(stillFailed));
    }
    setRetrying(false);
  };

  const handleDismiss = () => {
    sessionStorage.removeItem(key);
    setFailed([]);
  };

  return (
    <div className="ci-banner">
      <IconAlertTriangle className="ci-banner__icon" size={18} stroke={1.5} />
      <div className="ci-banner__body">
        <div className="ci-banner__title">
          {failed.length} device{failed.length === 1 ? '' : 's'} failed to create
        </div>
        <div className="ci-banner__text">
          The session was saved, but some devices didn&apos;t make it. Retry to try again.
        </div>
      </div>
      <div className="ci-banner__actions">
        <button type="button" className="ci-btn ci-btn--warning ci-btn--sm" onClick={handleRetry} disabled={retrying}>
          {retrying ? 'Retrying…' : 'Retry'}
        </button>
        <button type="button" className="ci-textbtn" onClick={handleDismiss} disabled={retrying}>
          Dismiss
        </button>
      </div>
    </div>
  );
};
